import AbstractCustomerValueModel from '../abstract_customer_value_model';

class ProvationRoi extends AbstractCustomerValueModel {
  // IMPACT VARIABLES ==================

  minutes_of_documentation_saved_per_procedure() {
    return this.input.minutes_of_documentation_saved_per_procedure;
  }

  coding_minutes_saved_per_procedure() {
    return this.input.coding_minutes_saved_per_procedure;
  }

  reporting_hours_saved_per_month() {
    return this.input.reporting_hours_saved_per_month;
  }

  percentage_increase_in_revenue_due_to_improved_billing() {
    return this.input.percentage_increase_in_revenue_due_to_improved_billing / 100;
  }

  incremental_procedures_per_physician_per_month() {
    return this.input.incremental_procedures_per_physician_per_month;
  }

  percent_increase_in_volume_due_to_referring_physicians() {
    return this.input.percent_increase_in_volume_due_to_referring_physicians / 100;
  }

  paper_elimination_dollar_savings_per_procedure() {
    return this.input.paper_elimination_dollar_savings_per_procedure;
  }

  // USER INPUTS =======================

  // H8
  gi_procedures_per_year() {
    return this.input.gi_procedures_per_year;
  }

  // H11
  percentage_of_physicians_employed_directly_by_facility() {
    return this.input.percentage_of_physicians_employed_directly_by_facility / 100;
  }

  // H9
  gi_physician_salary() {
    return this.input.gi_physician_salary;
  }

  // H12
  billing_and_coding_administrator_salary() {
    return this.input.billing_and_coding_administrator_salary;
  }

  // H14
  medical_data_analyst_salary() {
    return this.input.medical_data_analyst_salary;
  }

  // H13
  reimbursement_per_procedure() {
    return this.input.reimbursement_per_procedure;
  }

  // H10
  gi_physicians_operating_in_the_facility() {
    return this.input.gi_physicians_operating_in_the_facility;
  }

  // Configurator - G14
  license_type() {
    return this.input.license_type;
  }

  // Configurator - G16
  financing_term() {
    return this.input.financing_term;
  }

  // Configurator - G18
  apex_price_per_procedure_note() {
    return this.input.apex_price_per_procedure_note;
  }

  // Configurator - G19
  apex_percentage_field_discount() {
    return this.input.apex_percentage_field_discount / 100;
  }

  // Configurator - G21
  apex_percentage_annual_increase() {
    return this.input.apex_percentage_annual_increase / 100;
  }

  // Configurator - G27
  apex_one_time_implementation_costs() {
    return this.input.apex_one_time_implementation_costs;
  }

  // Configurator - G28
  apex_other_one_time_implementation_costs() {
    return this.input.apex_other_one_time_implementation_costs;
  }

  // Configurator - G33
  first_year_indirect_costs_to_migrate_to_provation() {
    return this.input.first_year_indirect_costs_to_migrate_to_provation;
  }

  // Configurator - I33
  ongoing_indirect_costs_to_migrate_to_provation() {
    return this.input.ongoing_indirect_costs_to_migrate_to_provation;
  }

  // Configurator - G22
  pvmd_percentage_annual_price_increase() {
    return this.input.pvmd_percentage_annual_price_increase / 100;
  }

  // Configurator - G29
  pvmd_total_one_time_software_costs() {
    return this.input.pvmd_total_one_time_software_costs;
  }

  // Configurator - G30
  pvmd_total_one_time_services_costs() {
    return this.input.pvmd_total_one_time_services_costs;
  }

  // Configurator - G31
  pvmd_total_third_party_one_time_services_costs() {
    return this.input.pvmd_total_third_party_one_time_services_costs;
  }

  // Configurator - G32
  pvmd_total_annual_recurring_maintenance_costs() {
    return this.input.pvmd_total_annual_recurring_maintenance_costs;
  }

  adoption_rate_year_1() {
    return this.input.adoption_rate_year_1 / 100;
  }

  adoption_rate_year_2() {
    return this.input.adoption_rate_year_2 / 100;
  }

  adoption_rate_year_3() {
    return this.input.adoption_rate_year_3 / 100;
  }

  adoption_rate_year_4() {
    return this.input.adoption_rate_year_4 / 100;
  }

  adoption_rate_year_5() {
    return this.input.adoption_rate_year_5 / 100;
  }

  // DERIVED INPUTS ====================

  // Configurator - G20
  discounted_price_per_procedure_note() {
    return this.apex_price_per_procedure_note() * (1 - this.apex_percentage_field_discount());
  }

  // STATIC INPUTS =====================

  // H15 (decided to make static)
  hours_per_month_on_all_forms_of_reporting() {
    return 60;
  }

  // Configurator - G42
  multiplier_for_fully_burdened_fte_cost() {
    return 1.35;
  }

  // Configurator - O52
  annual_hours_worked() {
    return 1845;
  }

  // VALUE DRIVER LABELS ===============

  increase_efficiencies_label() {
    return this.input.increase_efficiencies_label;
  }

  improve_accuracy_label() {
    return this.input.improve_accuracy_label;
  }

  // BENEFIT CALCULATIONS =========

  all_benefits() {
    return [
      'reduce_time_spent_by_physicians_on_procedure_documentation',
      'reduce_administrative_time_on_billing',
      'reduce_time_on_compliance_reporting',
      'eliminate_costs_of_paper_based_processes',
      'improve_reimbursement_levels_with_documentation',
      'increase_patient_throughput_with_streamlined_documentation',
      'increase_procedures_from_referring_physicians'
    ];
  }

  value_drivers() {
    return ['increase_efficiencies', 'improve_accuracy'];
  }

  increase_efficiencies() {
    return this.reduce_time_spent_by_physicians_on_procedure_documentation()
      + this.reduce_administrative_time_on_billing()
      + this.reduce_time_on_compliance_reporting()
      + this.eliminate_costs_of_paper_based_processes();
  }

  improve_accuracy() {
    return this.improve_reimbursement_levels_with_documentation()
      + this.increase_patient_throughput_with_streamlined_documentation()
      + this.increase_procedures_from_referring_physicians();
  }

  reduce_time_spent_by_physicians_on_procedure_documentation() {
    return this.percentage_of_physicians_employed_directly_by_facility() * (
      this.gi_procedures_per_year() * (this.minutes_of_documentation_saved_per_procedure() / 60)
    ) * (
      this.gi_physician_salary() * (this.multiplier_for_fully_burdened_fte_cost() / this.annual_hours_worked())
    );
  }

  reduce_administrative_time_on_billing() {
    return (
      this.gi_procedures_per_year() * (this.coding_minutes_saved_per_procedure() / 60)
    ) * (
      this.billing_and_coding_administrator_salary() * (this.multiplier_for_fully_burdened_fte_cost()
        / this.annual_hours_worked())
    );
  }

  reduce_time_on_compliance_reporting() {
    const admin_hourly_cost = this.medical_data_analyst_salary() * (
      this.multiplier_for_fully_burdened_fte_cost() / this.annual_hours_worked()
    );
    const pre = this.hours_per_month_on_all_forms_of_reporting() * 12 * admin_hourly_cost;
    const reduced_hours = this.hours_per_month_on_all_forms_of_reporting() - this.reporting_hours_saved_per_month();
    const post = reduced_hours * 12 * admin_hourly_cost;
    return pre - post;
  }

  eliminate_costs_of_paper_based_processes() {
    return this.gi_procedures_per_year() * this.paper_elimination_dollar_savings_per_procedure();
  }

  improve_reimbursement_levels_with_documentation() {
    return this.gi_procedures_per_year() * this.reimbursement_per_procedure()
      * this.percentage_increase_in_revenue_due_to_improved_billing();
  }

  increase_patient_throughput_with_streamlined_documentation() {
    return this.gi_physicians_operating_in_the_facility() * 12 * this.reimbursement_per_procedure()
      * this.incremental_procedures_per_physician_per_month();
  }

  increase_procedures_from_referring_physicians() {
    return this.gi_procedures_per_year() * this.reimbursement_per_procedure()
      * this.percent_increase_in_volume_due_to_referring_physicians();
  }

  adoption_rate(year) {
    switch (year) {
      case 1: return this.adoption_rate_year_1();
      case 2: return this.adoption_rate_year_2();
      case 3: return this.adoption_rate_year_3();
      case 4: return this.adoption_rate_year_4();
      case 5: return this.adoption_rate_year_5();
      default: return 1.0;
    }
  }

  // INVESTMENT CALCULATIONS ===========

  total_annual_investment(year) {
    if (this.license_type() === 'Apex') {
      return this.apex_total_annual_investment(year);
    } if (this.license_type() === 'PVMD') {
      return this.pvmd_total_annual_investment(year);
    }
    return 0;
  }

  apex_total_annual_investment(year) {
    const annual_license_costs = this.discounted_price_per_procedure_note() * this.gi_procedures_per_year();
    if (year === 1) {
      return annual_license_costs + this.apex_one_time_implementation_costs()
        + this.apex_other_one_time_implementation_costs() + this.first_year_indirect_costs_to_migrate_to_provation();
    }
    const annual_price_increase = (1 + this.apex_percentage_annual_increase()) ** (year - 1);
    return this.ongoing_indirect_costs_to_migrate_to_provation()
        + (annual_license_costs * annual_price_increase);
  }

  pvmd_total_annual_investment(year) {
    if (year === 1) {
      return this.pvmd_total_annual_recurring_maintenance_costs() + this.pvmd_total_one_time_software_costs()
        + this.pvmd_total_one_time_services_costs() + this.pvmd_total_third_party_one_time_services_costs()
        + this.first_year_indirect_costs_to_migrate_to_provation();
    }
    const annual_price_increase = (1 + this.pvmd_percentage_annual_price_increase()) ** (year - 1);
    return this.ongoing_indirect_costs_to_migrate_to_provation()
        + (this.pvmd_total_annual_recurring_maintenance_costs() * annual_price_increase);
  }
}

export default ProvationRoi;
